// Lightweight frequency-band VAD — ported verbatim from voice.js VoiceRecordingVAD.
// Discards garbage recordings (pure silence/noise) client-side; the backend
// Silero VAD is the real filter. Framework-agnostic — driven by the recorder hook.

interface VadOptions {
  fftSize?: number
  minSpeechFreq?: number
  maxSpeechFreq?: number
  threshold?: number
  hangoverTime?: number
}

export class VoiceRecordingVAD {
  fftSize: number
  minSpeechFreq: number
  maxSpeechFreq: number
  threshold: number
  hangoverTime: number

  audioContext: AudioContext | null = null
  analyser: AnalyserNode | null = null
  sourceNode: MediaStreamAudioSourceNode | null = null
  isActive = false
  isSpeaking = false
  hasVoice = false
  silenceTimer: ReturnType<typeof setTimeout> | null = null

  onVolumeUpdate: ((normalized: number) => void) | null = null
  onSpeechState: ((speaking: boolean) => void) | null = null

  constructor(options: VadOptions = {}) {
    this.fftSize = options.fftSize || 1024
    this.minSpeechFreq = options.minSpeechFreq || 300 // Hz — below this is hum/rumble
    this.maxSpeechFreq = options.maxSpeechFreq || 3000 // Hz — above this is hiss
    this.threshold = options.threshold || 45 // amplitude 0–255
    this.hangoverTime = options.hangoverTime || 1000 // ms grace between words
  }

  get isVoiceDetected(): boolean {
    return this.hasVoice
  }

  async start(stream: MediaStream): Promise<void> {
    this.hasVoice = false
    this.isSpeaking = false

    const Ctx = window.AudioContext || window.webkitAudioContext
    this.audioContext = new Ctx()
    this.sourceNode = this.audioContext.createMediaStreamSource(stream)
    this.analyser = this.audioContext.createAnalyser()
    this.analyser.fftSize = this.fftSize
    this.analyser.smoothingTimeConstant = 0.8
    this.sourceNode.connect(this.analyser)

    this.isActive = true
    this._loop()
  }

  _loop(): void {
    if (!this.isActive || !this.analyser || !this.audioContext) return

    const bufferLength = this.analyser.frequencyBinCount
    const freqData = new Uint8Array(bufferLength)
    this.analyser.getByteFrequencyData(freqData)

    const sampleRate = this.audioContext.sampleRate
    let speechEnergySum = 0
    let speechBinCount = 0
    let totalEnergy = 0

    for (let i = 0; i < bufferLength; i++) {
      const frequency = (i * sampleRate) / this.fftSize
      totalEnergy += freqData[i]
      if (frequency >= this.minSpeechFreq && frequency <= this.maxSpeechFreq) {
        speechEnergySum += freqData[i]
        speechBinCount++
      }
    }

    const speechEnergy = speechBinCount > 0 ? speechEnergySum / speechBinCount : 0
    const normalized = Math.min(1, totalEnergy / bufferLength / 128)
    if (this.onVolumeUpdate) this.onVolumeUpdate(normalized)

    if (speechEnergy > this.threshold) this._voiceDetected()
    else this._silenceDetected()

    requestAnimationFrame(() => this._loop())
  }

  _voiceDetected(): void {
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer)
      this.silenceTimer = null
    }
    if (!this.isSpeaking) {
      this.isSpeaking = true
      this.hasVoice = true
      if (this.onSpeechState) this.onSpeechState(true)
    }
  }

  _silenceDetected(): void {
    if (this.isSpeaking && !this.silenceTimer) {
      this.silenceTimer = setTimeout(() => {
        this.isSpeaking = false
        this.silenceTimer = null
        if (this.onSpeechState) this.onSpeechState(false)
      }, this.hangoverTime)
    }
  }

  stop(): void {
    this.isActive = false
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer)
      this.silenceTimer = null
    }
    if (this.sourceNode) {
      this.sourceNode.disconnect()
      this.sourceNode = null
    }
    if (this.analyser) {
      this.analyser.disconnect()
      this.analyser = null
    }
    if (this.audioContext) {
      this.audioContext.close().catch(() => {})
      this.audioContext = null
    }
  }
}
